import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getPracticeArchive, type ArchiveEntry } from '../services/practiceTestService';
import { getExamBlueprint, getExamSubjects, getExam } from '../config/regionConfig';
import { buildProvenanceLabel } from '../utils/provenanceLabel';
import { ArrowLeft, Trophy, Clock, BookOpen, RotateCcw, Calendar, ChevronRight, GraduationCap } from 'lucide-react';
import { Card } from '@branon-shared/ui/Card';
import { IconBadge } from '@branon-shared/ui/IconBadge';
import { EmptyState } from '@branon-shared/ui/EmptyState';

function formatTime(seconds: number | null): string {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function scorePercent(entry: ArchiveEntry): number {
  if (entry.max_marks && entry.max_marks > 0) {
    return Math.round(((entry.marks_obtained ?? 0) / entry.max_marks) * 100);
  }
  if (entry.total_questions > 0) {
    return Math.round((entry.score / entry.total_questions) * 100);
  }
  return 0;
}

// One continuous-flow sitting (SAT/JEE/NEET) submits a row per subject but is one "paper" —
// group rows so counts match the "X/20" language on the exam start pages. Board-exam rows
// (and retakes, which are always their own standalone action) each stay their own group.
function groupBySitting(list: ArchiveEntry[]): ArchiveEntry[][] {
  const groups: ArchiveEntry[][] = [];
  const byExam = new Map<string, ArchiveEntry[]>();

  for (const entry of list) {
    if (entry.is_retake) {
      groups.push([entry]);
      continue;
    }
    const arr = byExam.get(entry.exam_type);
    if (arr) arr.push(entry);
    else byExam.set(entry.exam_type, [entry]);
  }

  for (const [examType, examEntries] of byExam) {
    const subjectCount = getExamSubjects(examType).length;
    if (!getExam(examType)?.continuousFlow || subjectCount <= 1) {
      for (const e of examEntries) groups.push([e]);
      continue;
    }

    // Rows sharing a session_id are one sitting for sure.
    const bySession = new Map<string, ArchiveEntry[]>();
    const legacy: ArchiveEntry[] = [];
    for (const e of examEntries) {
      if (e.session_id) {
        const arr = bySession.get(e.session_id);
        if (arr) arr.push(e);
        else bySession.set(e.session_id, [e]);
      } else {
        legacy.push(e);
      }
    }
    groups.push(...bySession.values());

    // Legacy rows predate session_id, but every completed continuous-flow
    // attempt always wrote exactly one row per subject in the same instant —
    // so batching them chronologically in groups of subjectCount recovers
    // the same sittings without it.
    legacy.sort((a, b) => new Date(a.completed_at).getTime() - new Date(b.completed_at).getTime());
    for (let i = 0; i < legacy.length; i += subjectCount) {
      groups.push(legacy.slice(i, i + subjectCount));
    }
  }

  return groups;
}

function groupPercent(group: ArchiveEntry[]): number {
  const hasMarks = group.every(e => e.max_marks != null && e.max_marks > 0);
  if (hasMarks) {
    const obtained = group.reduce((s, e) => s + (e.marks_obtained ?? 0), 0);
    const max = group.reduce((s, e) => s + (e.max_marks ?? 0), 0);
    return max > 0 ? Math.round((obtained / max) * 100) : 0;
  }
  const score = group.reduce((s, e) => s + e.score, 0);
  const total = group.reduce((s, e) => s + e.total_questions, 0);
  return total > 0 ? Math.round((score / total) * 100) : 0;
}

function scoreColor(pct: number) {
  if (pct >= 80) return 'text-success bg-success-soft border-success';
  if (pct >= 50) return 'text-warning bg-warning-soft border-warning';
  return 'text-critical bg-critical-soft border-critical';
}

export default function PracticeArchive() {
  const { session, isLoading } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<ArchiveEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterExam, setFilterExam] = useState('all');

  useEffect(() => {
    if (!session) return;
    getPracticeArchive(session.student_id).then((data) => {
      setEntries(data);
      setLoading(false);
    });
  }, [session?.student_id]);

  if (isLoading || loading) {
    return (
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-coral mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading your past papers…</p>
      </main>
    );
  }

  const uniqueExams = [...new Set(entries.map(e => e.exam_type))].sort();
  const filtered = filterExam === 'all' ? entries : entries.filter(e => e.exam_type === filterExam);

  // Group fresh vs retakes
  const freshEntries = filtered.filter(e => !e.is_retake);
  const retakeEntries = filtered.filter(e => e.is_retake);

  // "Papers done" / filter counts are sittings, not raw rows — one JEE/SAT/NEET
  // session is 1 paper even though it wrote a row per subject.
  const allSittings = groupBySitting(entries);
  const freshSittings = groupBySitting(freshEntries);

  function handleRetake(entry: ArchiveEntry) {
    const bp = getExamBlueprint(entry.exam_type);
    navigate(`/practice-test/attempt/${entry.set_id}`, {
      state: {
        showTimer: true,
        examType: entry.exam_type,
        subject: entry.subject ?? undefined,
        blueprint: bp,
        isRetake: true,
      },
    });
  }

  function handleReview(entry: ArchiveEntry) {
    navigate(`/practice-test/results/${entry.attempt_id}`);
  }

  function EntryCard({ entry }: { entry: ArchiveEntry }) {
    const pct = scorePercent(entry);
    const hasMarks = entry.max_marks != null && entry.max_marks > 0;

    return (
      <Card style={{ padding: '1rem 1.25rem' }}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <IconBadge icon={GraduationCap} tone="night" size={36} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-quicksand font-bold text-ink text-sm">{entry.exam_type}</span>
                {entry.subject && (
                  <span className="text-xs text-gray-500 px-1.5 py-0.5 bg-gray-100 rounded-full">{entry.subject}</span>
                )}
                {entry.is_retake && (
                  <span className="text-xs text-coral px-1.5 py-0.5 bg-coral/10 rounded-full font-medium">Retake</span>
                )}
                <span className="text-[10px] text-gray-400">
                  {buildProvenanceLabel({ provenance: entry.predominant_provenance ?? 'ai_generated', exam_type: entry.exam_type })}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(entry.completed_at)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTime(entry.time_taken_seconds)}
                </span>
              </div>
            </div>
          </div>

          {/* Score badge */}
          <div className={`flex-shrink-0 px-3 py-1.5 rounded-card border-2 text-center min-w-[64px] ${scoreColor(pct)}`}>
            <div className="text-base font-extrabold leading-none">{pct}%</div>
            <div className="text-[10px] mt-0.5 font-medium">
              {hasMarks
                ? `${entry.marks_obtained ?? 0}/${entry.max_marks}m`
                : `${entry.score}/${entry.total_questions}`}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={() => handleReview(entry)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-coral bg-coral/10 hover:bg-coral/20 rounded-btn transition-colors"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Review
          </button>
          <button
            onClick={() => handleRetake(entry)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold text-ink bg-gray-100 hover:bg-gray-200 rounded-btn transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Retake
          </button>
        </div>
      </Card>
    );
  }

  return (
    <main className="max-w-2xl mx-auto w-full px-4 sm:px-6 pt-24 pb-8">
      <Link to="/practice-test" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-ink mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        Back to Exam Simulator
      </Link>

      <div className="mb-6">
        <h1 className="font-quicksand text-xl sm:text-2xl font-bold text-ink">My Past Papers</h1>
        <p className="text-gray-500 text-sm mt-1">
          {entries.length} attempt{entries.length !== 1 ? 's' : ''} — review answers or retake any paper
        </p>
      </div>

      {/* Summary stats */}
      {freshSittings.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card style={{ padding: '0.75rem', textAlign: 'center' }}>
            <div className="text-xl font-extrabold text-ink">{freshSittings.length}</div>
            <div className="text-xs text-gray-500 mt-0.5">Papers done</div>
          </Card>
          <Card style={{ padding: '0.75rem', textAlign: 'center' }}>
            <div className="text-xl font-extrabold text-ink">
              {Math.round(freshSittings.reduce((s, g) => s + groupPercent(g), 0) / freshSittings.length)}%
            </div>
            <div className="text-xs text-gray-500 mt-0.5">Avg score</div>
          </Card>
          <Card style={{ padding: '0.75rem', textAlign: 'center' }}>
            <div className="text-xl font-extrabold text-ink">
              {Math.max(...freshSittings.map(groupPercent))}%
            </div>
            <div className="text-xs text-gray-500 mt-0.5">Best score</div>
          </Card>
        </div>
      )}

      {/* Exam filter */}
      {uniqueExams.length > 1 && (
        <div className="flex gap-2 mb-5 flex-wrap">
          <button
            onClick={() => setFilterExam('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterExam === 'all' ? 'bg-coral text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            All ({allSittings.length})
          </button>
          {uniqueExams.map(exam => (
            <button
              key={exam}
              onClick={() => setFilterExam(exam)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterExam === exam ? 'bg-coral text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {exam} ({groupBySitting(entries.filter(e => e.exam_type === exam)).length})
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <Card>
          <EmptyState icon={Trophy} title="No papers yet" subtitle="Complete an exam to see it here" />
          <div className="text-center -mt-4">
            <Link
              to="/practice-test"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-coral text-white rounded-btn text-sm font-bold border-2 border-ink shadow-sticker hover:translate-y-0.5 hover:shadow-none transition-all"
            >
              <ChevronRight className="w-4 h-4" />
              Start a Practice Test
            </Link>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Fresh attempts */}
          {freshEntries.length > 0 && (
            <>
              {retakeEntries.length > 0 && (
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Fresh Attempts</h2>
              )}
              {freshEntries.map(e => <EntryCard key={e.attempt_id} entry={e} />)}
            </>
          )}

          {/* Retakes */}
          {retakeEntries.length > 0 && (
            <>
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mt-5 mb-2">Retakes</h2>
              {retakeEntries.map(e => <EntryCard key={e.attempt_id} entry={e} />)}
            </>
          )}
        </div>
      )}
    </main>
  );
}
